import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

/**
 * ZoomableImage — Wraps an <img>. On double-click, opens a full-screen zoom modal.
 * Usage: <ZoomableImage src="..." alt="..." className="..." data-testid="..." />
 */
export const ZoomableImage = ({ src, alt, className, caption, ...rest }) => {
  const [open, setOpen] = useState(false);
  const [thumbError, setThumbError] = useState(false);

  const close = useCallback(() => setOpen(false), []);

  const webpSrc = src && src.startsWith('/generated/') && src.endsWith('.png')
    ? src.replace(/\.png$/, '.webp')
    : null;
  const thumbSrc = webpSrc && !thumbError ? webpSrc : src;

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close]);

  if (!src) return null;

  return (
    <>
      <img
        src={thumbSrc}
        alt={alt}
        onError={() => { if (!thumbError && webpSrc) setThumbError(true); }}
        decoding="async"
        onDoubleClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen(true); }}
        onClick={(e) => e.stopPropagation()}
        title="Double-cliquez pour agrandir"
        className={`cursor-zoom-in select-none ${className || ''}`}
        {...rest}
      />

      {open && createPortal(
        (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 cursor-zoom-out"
            onClick={close}
            data-testid="zoom-modal"
          >
            <button
              type="button"
              onClick={close}
              className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 border border-white/30 text-white flex items-center justify-center transition-colors"
              aria-label="Fermer"
              data-testid="zoom-modal-close"
            >
              <X size={22} />
            </button>

            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="max-w-[95vw] max-h-[92vh] flex flex-col items-center gap-3"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={src}
                alt={alt}
                className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
                data-testid="zoom-modal-image"
              />
              {(caption || alt) && (
                <p className="text-white/90 font-heading italic text-center text-sm md:text-base max-w-2xl px-4">
                  {caption || alt}
                </p>
              )}
            </motion.div>
          </motion.div>
        ),
        document.body
      )}
    </>
  );
};

export default ZoomableImage;
